import type { AttributeReviewFieldDecisionInput } from "@/graphql/generated";
import type { AttributeReviewDraftSelection } from "@/hooks/useAttributeReview.types";
import { newValueName, valueKey, type PoolValue } from "./valueView";

export function fieldDecisionFor(
  sourceFieldId: string,
  draft:
    | Pick<
        AttributeReviewDraftSelection,
        "kind" | "leaf" | "path" | "pool" | "values"
      >
    | undefined,
  shownValues: PoolValue[] = [],
): AttributeReviewFieldDecisionInput {
  const fixed = new Set(draft?.values?.map((fix) => fix.storeValue));
  // D161: an untouched new value is saved under the name its box shows, never the store's spelling.
  const named = shownValues
    .filter((v) => !v.ignored && v.match === "new" && !fixed.has(v.store_value))
    .map((v) => ({ v, label: newValueName(v) }))
    .filter(({ v, label }) => label !== v.pool_value)
    .map(({ v, label }) => ({
      storeValue: v.store_value,
      newValue: { key: valueKey(label), label },
    }));
  const values = [...(draft?.values ?? []), ...named];
  return {
    sourceFieldId,
    decision: "APPROVE",
    ...(draft?.kind ? { kind: draft.kind } : {}),
    ...(draft?.leaf ? { leaf: draft.leaf } : {}),
    ...(draft?.path ? { path: draft.path } : {}),
    ...(draft?.pool ? { pool: draft.pool } : {}),
    ...(values.length ? { values } : {}),
  };
}

// D167: a free-text row saves the list it shows, so the list lands on the field.
export function fieldPoolOfText(
  row: { sourceEnumValueCount?: unknown },
  pool: { key?: string } | null | undefined,
): string | undefined {
  return Number(row.sourceEnumValueCount ?? 0) === 0 ? pool?.key : undefined;
}

export function undoDecisionFor(
  sourceFieldId: string,
  draft: Parameters<typeof fieldDecisionFor>[1],
  finished: boolean,
): AttributeReviewFieldDecisionInput {
  // A finished session refuses CLEAR, so Undo approves the row again.
  return finished
    ? fieldDecisionFor(sourceFieldId, draft)
    : { sourceFieldId, decision: "CLEAR" };
}

export function kindWarning(
  answers: { kind?: string | null; needs_review?: boolean }[],
  pickedKind: string | null,
  choices: string[],
): string | null {
  const [first, second] = answers;
  const agreed =
    answers.length === 2 &&
    !answers.some((a) => a.needs_review) &&
    first.kind === second.kind
      ? first.kind
      : null;
  return agreed &&
    choices.includes(agreed) &&
    pickedKind &&
    pickedKind !== agreed
    ? `Both models said "${agreed}". You picked "${pickedKind}".`
    : null;
}
